import type { Rng } from './rng.js'

/**
 * Room code alphabet.
 *
 * No I, O, 0 or 1. These codes get read aloud across a living room and typed
 * by an eight-year-old on a phone, and every one of those four is routinely
 * mistaken for another. Dropping them costs a little entropy and saves a lot
 * of "no, the letter one".
 */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

export const ROOM_CODE_LENGTH = 4

export function generateRoomCode(rng: Rng): string {
  return Array.from(
    { length: ROOM_CODE_LENGTH },
    () => ROOM_CODE_ALPHABET[rng.int(ROOM_CODE_ALPHABET.length)],
  ).join('')
}

/**
 * What each excluded character was most likely meant to be.
 *
 * The governing rule is that a substitution may only ever apply to a character
 * the alphabet excludes. Rewriting a character that is already valid would let
 * a correctly typed code be silently redirected into a *different* room — for
 * example if L were folded onto J, rooms PLKQ and PJKQ could both exist and a
 * player typing their own code correctly would land in the wrong one.
 */
const CONFUSABLE: Readonly<Record<string, string>> = {
  // Capital I and the digit 1 are both read as a lowercase l, which upper-cases to L.
  I: 'L',
  '1': 'L',
  // O and zero are both read as Q at a distance.
  O: 'Q',
  '0': 'Q',
}

/**
 * Cleans up what somebody typed.
 *
 * Forgiving on purpose: upper-cases, strips spaces and dashes, and maps the
 * excluded characters onto what the reader almost certainly meant. Somebody
 * squinting at a television and typing "o" should not get "no such room".
 *
 * Characters already in the alphabet are never touched — see CONFUSABLE.
 */
export function normaliseRoomCode(input: string): string {
  return [...input.toUpperCase().replace(/[\s-]/g, '')]
    .map((character) => CONFUSABLE[character] ?? character)
    .join('')
    .slice(0, ROOM_CODE_LENGTH)
}

export function isRoomCode(value: string): boolean {
  if (value.length !== ROOM_CODE_LENGTH) return false
  return [...value].every((character) => ROOM_CODE_ALPHABET.includes(character))
}
